import { Dayjs } from 'dayjs';

import { Appointment, CalendarEventTypeEnum, Customer, Shift, Service, AppointmentEvent } from 'types';
import { GetAppointmentCustomer, GetAppointmentServices } from '../index';
import { AppointmentFilters, CalendarEvent, CalendarResource } from '../../AppointmentsCalendar/types';
import { mapServiceIdsToServices, sortByServicesCompareFn, TimeHelper } from '../../../utils';
import { ResourceTypeEnum } from '../../../types/ResourceTypeEnum';

type OverflowResources = {
  periods: { from: number; to: number }[];
  num: number;
}[];

export type CalendarData = {
  events: CalendarEvent[];
  resources: CalendarResource[];
  eventTypeCounters: { [key: string]: number };
};

function prepareCustomer(customer?: Customer): CalendarEvent['customer'] {
  return {
    name: customer?.name || '',
    lastName: customer?.lastName || '',
    phoneNumber: customer?.phoneNumber || '',
    email: customer?.email || '',
    gender: customer?.gender || undefined,
  };
}

function canAppointmentBeAddedToOverflow(appointmentType: CalendarEventTypeEnum = CalendarEventTypeEnum.APPOINTMENT) {
  // todo add tasks and other types
  return [
    CalendarEventTypeEnum.APPOINTMENT,
    CalendarEventTypeEnum.WALKIN,
    CalendarEventTypeEnum.BREAK,
    CalendarEventTypeEnum.BLOCKER,
    CalendarEventTypeEnum.TASK,
  ].includes(appointmentType);
}

function findAmountOfEventsForResourceByType(events: CalendarEvent[]): CalendarResource['eventsStatistics'] {
  return events.reduce((acc, curr) => {
    acc[curr.type] = (acc[curr.type] || 0) + 1;
    return acc;
  }, {});
}

function composeEventTitle(genderTitles: { male: string; female: string }, event: Appointment, customer?: Customer): string {
  if (!customer) {
    if (event.title) {
      return event.title;
    }
    return '';
  }
  const name = `${customer.name} ${customer.lastName}`;
  if (customer.gender) {
    const prefix = customer.gender === 'male' ? genderTitles.male : genderTitles.female;
    return `${prefix} ${name}`;
  }

  return name;
}

function getOverFlowResourceId(start: number, end: number, overflowResources: OverflowResources) {
  let overflowResourceNum = 0;
  const from = start;
  const to = end;
  const existingOverflow = overflowResources.find((resource) => {
    if (resource.periods.length < 1) {
      return true;
    }
    const overlappingPeriod = resource.periods.find((period) => {
      if (period.from < from) {
        if (period.to <= from) {
          // periods: [{from: 2, to: 3}], event: {from: 3, to: 4}
          // no overlap
          return false;
        }
        // periods: [{from: 2, to: 6}], event: {from: 3, to: 4}
        // overlap
        return true;
      }
      if (period.from > from) {
        if (period.from < to) {
          // periods: [{from: 3.5, to: 6}], event: {from: 3, to: 4}
          // overlap
          return true;
        }
        // periods: [{from: 4, to: 6}], event: {from: 3, to: 4}
        // no overlap
        return false;
      }
      // periods: [{from: 4, to: 6}], event: {from: 4, to: 4.5}
      // overlap
      return true;
    });
    return !overlappingPeriod;
  });
  if (existingOverflow) {
    overflowResourceNum = existingOverflow.num;
  } else {
    overflowResourceNum = overflowResources.length;
    overflowResources.push({ num: overflowResourceNum, periods: [] });
  }
  overflowResources[overflowResourceNum].periods.push({ from, to });
  return overflowResourceNum;
}

function parseEmployeeId(originalEmployeeId: string) {
  const [prefixOrEmployeeId, employeeId] = originalEmployeeId.split('-');

  if (prefixOrEmployeeId && employeeId) {
    return {
      prefix: prefixOrEmployeeId,
      employeeId,
    };
  }

  return {
    prefix: '',
    employeeId: prefixOrEmployeeId,
  };
}

export function buildAppointmentEvents(
  appointments: Appointment[],
  shifts: Shift[],
  employeeNameById: { [employeeId: string]: string },
  allServices: Service[],
  date: Dayjs,
  slotsData: any,
  timezone: string,
  translations: { male: string; female: string; overflow: string },
  filters?: AppointmentFilters,
): CalendarData {
  const eventTypeCounters = {
    [CalendarEventTypeEnum.APPOINTMENT.toString()]: 0,
    [CalendarEventTypeEnum.TASK.toString()]: 0,
    [CalendarEventTypeEnum.BREAK.toString()]: 0,
    [CalendarEventTypeEnum.BLOCKER.toString()]: 0,
  };

  const filteredServices = filters?.services.map((service) => service.extra.id);
  const filteredType = filters?.focus;
  const overflowResources: OverflowResources = [];
  const overflowEmployeeResources: { [employeeId: string]: OverflowResources } = {};
  const overbookingEmployeeResources: { [employeeId: string]: OverflowResources } = {};
  const events: any[] = [];
  const resourceEventCounters: { [id: string]: number } = {};
  const dateStartTimestamp = date.startOf('day').valueOf();
  const dateStr = TimeHelper.toStandardFormat(date);

  function addToCounters(assignment: string): void {
    if (!resourceEventCounters[assignment]) {
      resourceEventCounters[assignment] = 0;
    }
    resourceEventCounters[assignment] += 1;
  }

  function addToOverflows(appointment: Appointment, serviceList: Service[], assignment?: string, suggestedEmployee?: string): string {
    const isOverflow = (assignment === 'missed' || !assignment) && canAppointmentBeAddedToOverflow(appointment.type);
    const startTimestamp = dateStartTimestamp + appointment.time * 60000;
    const endTimestamp = startTimestamp + appointment.duration * 60000;
    let resourceId;
    if (isOverflow) {
      if (
        filteredType?.length &&
        !filteredType.filter((eventType) => eventType !== CalendarEventTypeEnum.APPOINTMENT).includes(appointment.type!) &&
        !filteredServices?.length
      ) {
        return '';
      }
      if (filteredServices && filteredServices.length > 0) {
        if (serviceList.length < 1) {
          return '';
        }
        if (!serviceList.every((serviceId) => filteredServices.includes(serviceId.id))) {
          return '';
        }
      }
      const isOverflowWithEmployee = isOverflow && !!suggestedEmployee;
      if (isOverflowWithEmployee) {
        if (!overflowEmployeeResources[suggestedEmployee]) {
          overflowEmployeeResources[suggestedEmployee] = [];
        }
        const overflowResourceId = getOverFlowResourceId(startTimestamp, endTimestamp, overflowEmployeeResources[suggestedEmployee]);
        resourceId = `employee-${ResourceTypeEnum.OVERFLOW}-${suggestedEmployee}-${overflowResourceId}`;
      } else {
        resourceId = `${ResourceTypeEnum.OVERFLOW}-${getOverFlowResourceId(startTimestamp, endTimestamp, overflowResources)}`;
      }
    }
    return resourceId;
  }

  function addToOverbookings(
    appointment: Appointment,
    serviceList: Service[],
    pinnedEmployee?: string,
  ): string {
    const startTimestamp = dateStartTimestamp + appointment.time * 60000;
    const endTimestamp = startTimestamp + appointment.duration * 60000;
    let resourceId;
    if (
      filteredType?.length &&
      !filteredType.includes(appointment.type as any) &&
      !filteredServices?.length
    ) {
      return '';
    }
    if (filteredServices && filteredServices.length > 0) {
      if (serviceList.length < 1) {
        return '';
      }
      if (!serviceList.every((serviceId) => filteredServices.includes(serviceId.id))) {
        return '';
      }
    }
    const isOverflowWithEmployee = !!pinnedEmployee;
    if (isOverflowWithEmployee) {
      if (!overbookingEmployeeResources[pinnedEmployee]) {
        overbookingEmployeeResources[pinnedEmployee] = [];
      }
      const overflowResourceId = getOverFlowResourceId(startTimestamp, endTimestamp, overbookingEmployeeResources[pinnedEmployee]);
      resourceId = `employee-${ResourceTypeEnum.OVERBOOKING}-${pinnedEmployee}-${overflowResourceId}`;
    }
    return resourceId;
  }

  appointments.forEach((appointment) => {
    if (appointment.date !== dateStr) {
      return;
    }
    if (appointment.type) {
      eventTypeCounters[appointment.type] += 1;
    }
    const { duration, title, pinnedEmployee: pinnedEmployeeRaw } = appointment;
    let pinnedEmployee: string | undefined;
    let isOverbooking = false;
    if (pinnedEmployeeRaw) {
      const parsedEmployeeId = parseEmployeeId(pinnedEmployeeRaw);
      pinnedEmployee = parsedEmployeeId.employeeId;
      isOverbooking = parsedEmployeeId.prefix === 'overbooking';
    }
    const serviceList: Service[] = GetAppointmentServices(appointment, allServices);
    const customer = GetAppointmentCustomer(appointment);
    const startTime = appointment.time;
    const endTime = startTime + duration;
    const startOfDayLocal = TimeHelper.getStartOfDayLocal(appointment.date);
    const startDate = new Date(TimeHelper.setMinutesLocal(startOfDayLocal, appointment.time));
    const endDate = new Date(TimeHelper.setMinutesLocal(startOfDayLocal, endTime));

    let isOverflow = false;
    let resourceId = 'missed';
    let suggestedEmployee;
    if (appointment.type && appointment.eventTypeData && (appointment.eventTypeData as { suggestedEmployee?: string }).suggestedEmployee) {
      suggestedEmployee = (appointment.eventTypeData as { suggestedEmployee: string }).suggestedEmployee;
    }

    if (isOverbooking) {
      const overbookingResourceId = addToOverbookings(appointment, serviceList, pinnedEmployee);
      if (overbookingResourceId) {
        if (!resourceEventCounters[overbookingResourceId]) {
          resourceEventCounters[overbookingResourceId] = 0;
        }
        resourceEventCounters[overbookingResourceId] += 1;
        isOverflow = true;
        resourceId = overbookingResourceId;
      }
    }

    if (resourceId === 'missed') {
      if (pinnedEmployee && shifts.find((shift) => shift.employeeId === pinnedEmployee)) {
        resourceId = pinnedEmployee;
      }
      addToCounters(resourceId);
      const overflowResourceId = addToOverflows(
        appointment,
        serviceList,
        resourceId,
        suggestedEmployee,
      );
      if (overflowResourceId) {
        isOverflow = true;
        resourceId = overflowResourceId;
      }
    }

    const eventTypeData = appointment.eventTypeData as AppointmentEvent;

    events.push({
      resourceId,
      id: appointment.id,
      title: composeEventTitle(translations, appointment, customer),
      appointmentTitle: title,
      start: startDate,
      startFormatted: TimeHelper.getHHmmTimeFromMinutes(startTime),
      end: endDate,
      endFormatted: TimeHelper.getHHmmTimeFromMinutes(endTime),
      isProtected: appointment.isProtected || false,
      duration,
      services: serviceList,
      customer: prepareCustomer(customer),
      type: appointment.type,
      suggestedEmployee: suggestedEmployee || undefined,
      isOverflow,
      timeFormat: 'HH:mm',
      metadata: appointment.metadata,
      note: appointment.note,
      status: appointment.status,
      date: appointment.date,
      time: appointment.time,
      splitService: eventTypeData?.split ? true : false,
    });
  });

  const slotEvents: any[] = [];
  const dateString = TimeHelper.toStandardFormat(date);

  const slots = slotsData[dateString];
  if (slots) {
    const employeeIds = Object.keys(slots);
    const employeeLen = employeeIds.length;
    for (let employeeIndex = 0; employeeIndex < employeeLen; employeeIndex += 1) {
      const originalEmployeeId = employeeIds[employeeIndex];
      const { prefix, employeeId } = parseEmployeeId(originalEmployeeId)!;

      const currentEmployee: string = employeeId;
      const isOverbooking: boolean = prefix === 'overbooking';
      const currentSlots = slots[originalEmployeeId];
      if (!resourceEventCounters[currentEmployee]) {
        resourceEventCounters[currentEmployee] = 0;
      }
      if (currentSlots.length > 0) {
        currentSlots.forEach((timeSlot) => {
          const startTimestamp = dateStartTimestamp + timeSlot.time * 60000;
          const endTimestamp = startTimestamp + timeSlot.duration * 60000;
          const startMinutes = timeSlot.time;
          const endMinutes = startMinutes + timeSlot.duration;

          const startOfDayLocalSlot = TimeHelper.getStartOfDayLocal(dateString);
          const startDate = new Date(TimeHelper.setMinutesLocal(startOfDayLocalSlot, timeSlot.time));
          const endDate = new Date(TimeHelper.setMinutesLocal(startOfDayLocalSlot, timeSlot.time + timeSlot.duration));

          const overlappingEvent = events.find(
            (event) =>
              event.resourceId === currentEmployee &&
              ((event.start <= startDate && event.end > startDate) || (event.start < endDate && event.end >= endDate)),
          );

          if ((overlappingEvent || currentEmployee === 'missed')) {
            if (filteredType?.length) {
              return;
            }
            if (filteredServices && filteredServices.length > 0) {
              if (timeSlot.services.length < 1) {
                return;
              }
              if (!timeSlot.services.every((serviceId) => filteredServices.includes(serviceId.id))) {
                return;
              }
            }
            const overFlowIn = getOverFlowResourceId(startTimestamp, endTimestamp, overflowResources);
            resourceEventCounters[currentEmployee] += 1;
            slotEvents.push({
              resourceId: `${ResourceTypeEnum.OVERFLOW}-${overFlowIn}`,
              id: `${CalendarEventTypeEnum.SLOT}-${currentEmployee}-${timeSlot.time}`,
              title: '',
              appointmentTitle: '[appointmentTitle]',
              start: startDate,
              startFormatted: TimeHelper.getHHmmTimeFromMinutes(startMinutes),
              duration: timeSlot.duration,
              end: endDate,
              endFormatted: TimeHelper.getHHmmTimeFromMinutes(endMinutes),
              services: mapServiceIdsToServices(allServices, timeSlot.services),
              type: CalendarEventTypeEnum.SLOT,
              timeFormat: 'HH:mm',
              relatedSlotId: timeSlot.relatedSlotId,
              relationOrder: timeSlot.relationOrder,
              date: dateString,
              time: timeSlot.time,
            });
          } else if (isOverbooking) {
            if (Array.isArray(filteredType) && filteredType.length > 0 && filteredType.indexOf(CalendarEventTypeEnum.APPOINTMENT) === -1) {
              return;
            }
            if (filteredServices && filteredServices.length > 0) {
              if (timeSlot.services.length < 1) {
                return;
              }
              if (!timeSlot.services.every((serviceId) => filteredServices.includes(serviceId))) {
                return;
              }
            }
            if (!overbookingEmployeeResources[currentEmployee]) {
              overbookingEmployeeResources[currentEmployee] = [];
            }
            const overBookingIn = getOverFlowResourceId(startTimestamp, endTimestamp, overbookingEmployeeResources[currentEmployee]);
            resourceEventCounters[currentEmployee] += 1;
            slotEvents.push({
              resourceId: `employee-${ResourceTypeEnum.OVERBOOKING}-${currentEmployee}-${overBookingIn}`,
              id: `${CalendarEventTypeEnum.SLOT}-${currentEmployee}-${timeSlot.time}`,
              title: '',
              appointmentTitle: '[appointmentTitle]',
              start: startDate,
              startFormatted: TimeHelper.getHHmmTimeFromMinutes(startMinutes),
              duration: timeSlot.duration,
              end: endDate,
              endFormatted: TimeHelper.getHHmmTimeFromMinutes(endMinutes),
              services: mapServiceIdsToServices(allServices, timeSlot.services),
              type: CalendarEventTypeEnum.SLOT,
              timeFormat: 'HH:mm',
              relatedSlotId: timeSlot.relatedSlotId,
              relationOrder: timeSlot.relationOrder,
              date: dateString,
              time: timeSlot.time,
              isOverflow: true,
            });
          } else {
            slotEvents.push({
              resourceId: currentEmployee,
              id: `${CalendarEventTypeEnum.SLOT}-${currentEmployee}-${timeSlot.time}`,
              title: '',
              appointmentTitle: '[appointmentTitle]',
              start: startDate,
              startFormatted: TimeHelper.getHHmmTimeFromMinutes(startMinutes),
              duration: timeSlot.duration,
              end: endDate,
              endFormatted: TimeHelper.getHHmmTimeFromMinutes(endMinutes),
              services: mapServiceIdsToServices(allServices, timeSlot.services),
              type: CalendarEventTypeEnum.SLOT,
              timeFormat: 'HH:mm',
              relatedSlotId: timeSlot.relatedSlotId,
              relationOrder: timeSlot.relationOrder,
              date: dateString,
              time: timeSlot.time,
            });
          }
        });
      }
    }
  }

  events.push(...slotEvents);

  const shiftsPerEmployee = shifts.reduce((acc: Record<string, Shift[]>, shift) => {
    if (!acc[shift.employeeId]) {
      acc[shift.employeeId] = [];
    }
    acc[shift.employeeId].push(shift);
    return acc;
  }, {});

  const resources: CalendarResource[] = Object.entries(shiftsPerEmployee)
    .reduce((acc: (CalendarResource & { shiftLength: number })[], [employeeId, employeeShifts]) => {
      const shift = employeeShifts.find((item) => date.isSame(item.start, 'day'));
      if (!shift) {
        return acc;
      }
      const services = mapServiceIdsToServices(allServices, shift.services);
      const shiftLength = shift.end - shift.start;

      const calendarShifts = employeeShifts.map((item) => {
        const startDateTime = TimeHelper.timestampToDateAndTimeTz(item.start, timezone);
        const endDateTime = TimeHelper.timestampToDateAndTimeTz(item.end, timezone);

        return {
          from: TimeHelper.getTimeByMinutes(startDateTime.time),
          to: TimeHelper.getTimeByMinutes(endDateTime.time),
          weekDay: TimeHelper.getWeekDay(item.start),
        };
      });

      acc.push({
        id: employeeId,
        title: employeeNameById[shift.employeeId],
        shifts: calendarShifts,
        shiftLength,
        services,
      });
      return acc;
    }, [])
    .sort((cur, next) => {
      let curServices: number[] = [];
      if (cur.services && cur.services.length) {
        curServices = cur.services.map((service) => service.displayOrder || 0);
      }
      let nextServices: number[] = [];
      if (next.services && next.services.length) {
        nextServices = next.services.map((service) => service.displayOrder || 0);
      }
      if (curServices.length && nextServices.length) {
        const sorted = sortByServicesCompareFn(curServices, nextServices);
        if (sorted !== 0) {
          return sorted;
        }
        // at this point either they are equal or one array longer than another, skip service ordering
      }

      const shiftSort = next.shiftLength - cur.shiftLength;
      if (shiftSort !== 0) {
        return shiftSort;
      }
      return 0;
    });

  const overbookingResourcesKeys = Object.keys(overbookingEmployeeResources);
  if (overbookingResourcesKeys.length > 0) {
    for (let resourceIndex = 0; resourceIndex < overbookingResourcesKeys.length; resourceIndex += 1) {
      const employeeId = overbookingResourcesKeys[resourceIndex];
      const employeeResources = overbookingEmployeeResources[employeeId];
      employeeResources.forEach((resource) => {
        const resourceId = `employee-${ResourceTypeEnum.OVERBOOKING}-${employeeId}-${resource.num}`;

        resources.push({
          id: resourceId,
          title: `${resourceIndex + 1}`,
          isOverflow: true,
          isEmployeeOverflow: true,
          overflowType: ResourceTypeEnum.OVERBOOKING,
          services: [],
          shifts: [],
          eventsStatistics: findAmountOfEventsForResourceByType(events.filter((event) => event.resourceId === resourceId)),
        });
      });
    }
  }

  if (overflowResources.length > 0) {
    resources.push(
      ...overflowResources.map((resource) => {
        const resourceId = `${ResourceTypeEnum.OVERFLOW}-${resource.num}`;
        return {
          id: resourceId,
          title: `${translations.overflow} ${resource.num + 1}`,
          isOverflow: true,
          overflowType: ResourceTypeEnum.OVERFLOW,
          services: [],
          shifts: [],
          eventsStatistics: findAmountOfEventsForResourceByType(events.filter((event) => event.resourceId === resourceId)),
        };
      }),
    );
  }
  if (Object.keys(overflowEmployeeResources).length > 0) {
    Object.entries(overflowEmployeeResources).map(([employeeId, employeeResources]) => {
      employeeResources.forEach((resource) => {
        const resourceId = `employee-${ResourceTypeEnum.OVERFLOW}-${employeeId}-${resource.num}`;
        let title = '';
        if (employeeId && employeeNameById[employeeId]) {
          title = employeeNameById[employeeId];
        }
        resources.push({
          id: resourceId,
          title,
          isOverflow: true,
          isEmployeeOverflow: true,
          overflowType: ResourceTypeEnum.OVERFLOW,
          services: [],
          shifts: [],
          eventsStatistics: findAmountOfEventsForResourceByType(events.filter((event) => event.resourceId === resourceId)),
        });
      });
    });
  }

  return {
    events,
    resources,
    eventTypeCounters,
  };
}

export function getFullNameWithPrefix(data: { name?: string; lastName?: string }, prefix?: string): string {
  if (!(data.name || data.lastName)) {
    return '';
  }

  return [prefix, data.name, data.lastName].filter(Boolean).join(' ');
}
